import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Sparkles, Clock, Loader2, FolderOpen, Trash2 } from "lucide-react"
import { trpc } from "../../lib/trpc"
import toast from "react-hot-toast"
import AuraPromptInput from "./components/AuraPromptInput"

const AuraScreen = () => {
  const navigate = useNavigate()
  const [prompt, setPrompt] = useState("")
  const [isGenerating, setIsGenerating] = useState(false)
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null)

  // tRPC mutations
  const utils = trpc.useUtils()
  const createAndGenerateMutation = trpc.aura.createProjectAndGenerate.useMutation()
  const deleteProjectMutation = trpc.aura.deleteProject.useMutation({
    onSuccess: () => {
      toast.success("Project deleted")
      void utils.aura.listProjects.invalidate()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete project")
    },
  })

  // Fetch recent projects
  const { data: recentProjects = [], isLoading: projectsLoading } = trpc.aura.listProjects.useQuery({
    limit: 6,
  })

  const handleSubmit = async (options: {
    message: string
    modelId: string
    styleReference?: { url?: string; imageUrl?: string }
  }) => {
    if (!options.message.trim() || isGenerating) return

    setIsGenerating(true)

    try {
      // Build the prompt - include style reference URL if provided
      let finalPrompt = options.message.trim()
      if (options.styleReference?.url) {
        finalPrompt = `${finalPrompt}\n\nStyle reference: ${options.styleReference.url}`
      }

      // Create project and generate (synchronous - waits for LLM response)
      const result = await createAndGenerateMutation.mutateAsync({
        prompt: finalPrompt,
        modelId: options.modelId,
      })

      // Check if generation completed successfully
      if (result.generationComplete) {
        // Navigate directly to editor - no polling needed
        navigate({
          to: `/aura/editor/${result.project.id}`,
          // @ts-ignore - TanStack router search params
          search: {
            conversationId: result.conversationId,
          },
        })
        toast.success("Design generated successfully!")
      } else {
        // Generation failed but project was created - navigate anyway
        navigate({
          to: `/aura/editor/${result.project.id}`,
          // @ts-ignore - TanStack router search params
          search: {
            conversationId: result.conversationId,
          },
        })
        toast.error(result.error || "Generation failed - you can retry from the editor")
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to create project")
    } finally {
      setIsGenerating(false)
    }
  }

  const handleOpenProject = (projectId: string) => {
    navigate({ to: `/aura/editor/${projectId}` })
  }

  const handleDeleteProject = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation()
    if (deletingProjectId) return // Already deleting

    if (window.confirm("Are you sure you want to delete this project?")) {
      setDeletingProjectId(projectId)
      deleteProjectMutation.mutate(
        { id: projectId },
        {
          onSettled: () => setDeletingProjectId(null),
        },
      )
    }
  }

  const formatDate = (date: Date | string) => {
    const d = typeof date === "string" ? new Date(date) : date
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffHours < 1) return "Just now"
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return d.toLocaleDateString()
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Hero Section */}
      <div className="flex flex-col items-center justify-center px-6 pt-20 pb-12">
        {/* Main Headline */}
        <h1 className="text-5xl md:text-7xl font-bold text-center mb-6 tracking-tight">Create beautiful designs</h1>

        {/* Subheadline */}
        <p className="text-xl text-gray-400 text-center mb-12">
          Generate top-tier landing pages in seconds.{" "}
          <button className="text-white underline underline-offset-4 hover:text-gray-300 transition">
            Watch video.
          </button>
        </p>

        {/* Prompt Input Card */}
        <div className="w-full max-w-3xl">
          <AuraPromptInput
            variant="hero"
            value={prompt}
            onChange={setPrompt}
            onSubmit={handleSubmit}
            isLoading={isGenerating}
          />
        </div>
      </div>

      {/* Recent Projects Section */}
      <div className="px-6 py-12 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-300">Recent Projects</h2>
          {recentProjects.length > 0 && (
            <button className="text-sm text-gray-500 hover:text-white transition">View all</button>
          )}
        </div>

        {projectsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
          </div>
        ) : recentProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FolderOpen className="w-12 h-12 text-gray-600 mb-4" />
            <p className="text-gray-500">No projects yet. Create your first design above!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentProjects.map((project) => (
              <div
                key={project.id}
                onClick={() => handleOpenProject(project.id)}
                className="group relative text-left bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-gray-700 hover:bg-gray-900/80 transition cursor-pointer"
              >
                {/* Delete Button */}
                <button
                  onClick={(e) => handleDeleteProject(e, project.id)}
                  disabled={deletingProjectId === project.id}
                  className="absolute top-2 right-2 p-1.5 bg-gray-800/80 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-500/20 hover:text-red-400 transition-all z-10"
                  title="Delete project"
                >
                  {deletingProjectId === project.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>

                {/* Thumbnail */}
                <div className="aspect-video bg-gray-800 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                  {project.thumbnail_url ? (
                    <img
                      src={project.thumbnail_url}
                      alt={project.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <Sparkles className="w-8 h-8 text-gray-600 group-hover:text-gray-500 transition" />
                  )}
                </div>

                {/* Content */}
                <h3 className="font-medium text-white truncate mb-1">{project.name}</h3>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  <span>{formatDate(project.updated_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default AuraScreen
